import {
  AdminMessage,
  type BaseMessage,
  FileMessage,
} from "@sendbird/chat/message";
import { DateTime } from "luxon";
import {
  ACTIVITY_CUSTOM_TYPE,
  ACTIVITY_INTERACTION_CUSTOM_TYPE,
  type AttentionContent,
  type AttentionMedia,
  type AttentionMessage,
  type AttentionQuote,
  type AttentionReadState,
  type AttentionSendState,
  type AttentionSender,
  ENTITY_INTERACTION_CUSTOM_TYPE,
  ENTITY_SHARED_CUSTOM_TYPE,
  GAME_END_CUSTOM_TYPE,
  GAME_START_CUSTOM_TYPE,
  GIF_CUSTOM_TYPE,
  SUPERVISION_CUSTOM_TYPE,
  isAttentionHidden,
  normalizeSenderRole,
  parseAttentionActivityPayload,
  parseAttentionEntityPayload,
  parseAttentionTakedown,
  resolveAttentionDirection,
} from "./attention-message";
import { deriveAttentionQuote } from "./attention-quote";

interface SendbirdSenderLike {
  userId: string;
  nickname?: string;
  profileUrl?: string;
  metaData?: { role?: string };
}

export interface AttentionMediaResolution {
  url?: string | null;
  pending?: boolean;
}

export interface AttentionTranscriptionResolution {
  transcription?: string | null;
  pending?: boolean;
}

/**
 * Sendbird adapter input. The resolvers are seams for data the raw message does
 * not carry: redacted display names, read receipts, signed media URLs, transcriptions.
 */
export interface AttentionAdapterContext {
  currentUserId: string;
  resolveSenderDisplayName?: (sender: SendbirdSenderLike) => string | null;
  /** Signs/redacts the sender avatar; falls back to the raw profile url. */
  resolveSenderAvatarUrl?: (sender: SendbirdSenderLike) => string | null;
  resolveReadState?: (message: BaseMessage) => AttentionReadState | undefined;
  resolveMediaUrl?: (message: FileMessage) => AttentionMediaResolution;
  resolveTranscription?: (
    message: FileMessage,
  ) => AttentionTranscriptionResolution;
}

function getSender(message: BaseMessage): SendbirdSenderLike | null {
  const sender = (message as { sender?: SendbirdSenderLike }).sender;
  return sender ?? null;
}

function getReqId(message: BaseMessage): string | null {
  const reqId = (message as { reqId?: string }).reqId;
  return reqId && reqId.length > 0 ? reqId : null;
}

/**
 * Stable row identity across the pending -> confirmed transition. Sendbird's
 * pending/failed messages all carry `messageId === 0`, so key them by `reqId`;
 * confirmed messages keep their real id so the row is never duplicated.
 */
export function getStableMessageId(message: BaseMessage): string {
  const messageId = message.messageId;
  if (typeof messageId === "number" && messageId > 0) return String(messageId);
  const reqId = getReqId(message);
  return reqId !== null ? `pending-${reqId}` : String(messageId);
}

/**
 * Reconciles confirmed (`allMessages`) and local (`localMessages`) lists as UIKit
 * does: confirmed first, then locals appended. A local whose `reqId` is already
 * confirmed is dropped so the succeeded message never duplicates its optimistic copy.
 */
export function mergeChannelMessages<T extends BaseMessage>(
  confirmed: readonly T[],
  local: readonly T[],
): T[] {
  if (local.length === 0) return [...confirmed];
  const confirmedReqIds = new Set<string>();
  for (const message of confirmed) {
    const reqId = getReqId(message);
    if (reqId !== null) confirmedReqIds.add(reqId);
  }
  const pending = local.filter((message) => {
    const reqId = getReqId(message);
    return reqId === null || !confirmedReqIds.has(reqId);
  });
  return [...confirmed, ...pending];
}

/**
 * UIKit GroupChannel pins sendingStatus !== "succeeded" to the end. Sort by
 * createdAt so cache leftovers (failed/none, including messageId 0 unsent)
 * sit in history. In-flight pending sends have createdAt ≈ now so they stay last.
 */
export function orderMessagesForAttention<T extends BaseMessage>(
  messages: readonly T[],
): T[] {
  return [...messages].sort((left, right) => left.createdAt - right.createdAt);
}

function toAttentionSender(
  sender: SendbirdSenderLike | null,
  ctx: AttentionAdapterContext,
): AttentionSender | null {
  if (!sender) return null;
  return {
    id: sender.userId,
    nickname: sender.nickname ?? null,
    displayName:
      ctx.resolveSenderDisplayName?.(sender) ?? sender.nickname ?? null,
    avatarUrl: ctx.resolveSenderAvatarUrl?.(sender) ?? sender.profileUrl ?? null,
    role: normalizeSenderRole(sender.metaData?.role),
  };
}

function getMimeType(message: FileMessage): string {
  return message.type ?? "";
}

function getThumbnailUrl(message: FileMessage): string | null {
  const thumbnail = message.thumbnails?.[0];
  return thumbnail?.url ?? thumbnail?.plainUrl ?? null;
}

function toPositive(value: number | undefined): number | null {
  return typeof value === "number" && value > 0 ? value : null;
}

function getThumbnailDimensions(message: FileMessage): {
  width: number | null;
  height: number | null;
} {
  const thumbnail = message.thumbnails?.[0];
  return {
    width: toPositive(thumbnail?.realWidth ?? thumbnail?.width),
    height: toPositive(thumbnail?.realHeight ?? thumbnail?.height),
  };
}

function toMedia(
  message: FileMessage,
  ctx: AttentionAdapterContext,
): AttentionMedia | null {
  const mimeType = getMimeType(message);
  const resolution = ctx.resolveMediaUrl?.(message);
  const url = resolution?.url ?? message.url ?? null;
  const pending = resolution?.pending ?? false;
  const thumbnailUrl = getThumbnailUrl(message);

  if (mimeType.includes("image")) {
    const { width, height } = getThumbnailDimensions(message);
    return {
      mediaType: "image",
      url,
      mimeType,
      thumbnailUrl,
      width,
      height,
      alt: message.name || null,
      pending,
    };
  }

  if (mimeType.includes("audio")) {
    const transcription = ctx.resolveTranscription?.(message);
    const messageId = message.messageId;
    return {
      mediaType: "audio",
      url,
      mimeType,
      isVoiceNote: true,
      sdkId:
        typeof messageId === "number" && messageId > 0
          ? String(messageId)
          : null,
      reqId: getReqId(message),
      durationSeconds: null,
      peaks: null,
      transcription: transcription?.transcription ?? null,
      pending,
    };
  }

  if (mimeType.includes("video") || mimeType.includes("mov")) {
    return {
      mediaType: "video",
      url,
      mimeType,
      posterUrl: thumbnailUrl,
      pending,
    };
  }

  return null;
}

// Sendbird sets a FileMessage body to the uploaded file name, so a body that is
// empty or equal to the file name means "no caption"; a distinct body is a real caption.
function toMediaCaption(text: string, fileName: string): string | null {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed === fileName.trim()) return null;
  return text;
}

function getTranslations(
  message: BaseMessage,
): Record<string, string> | null {
  const translations = (
    message as { translations?: Record<string, string> }
  ).translations;
  return translations && Object.keys(translations).length > 0
    ? translations
    : null;
}

function toContent(
  message: BaseMessage,
  ctx: AttentionAdapterContext,
): AttentionContent {
  const data = (message as { data?: string }).data;
  const customType = message.customType || "";
  const text = message.message ?? "";

  const takedown = parseAttentionTakedown(data);
  if (takedown) {
    return { type: "takedown", takedown };
  }

  if (isAttentionHidden(data)) {
    return { type: "hidden" };
  }

  if (customType === GIF_CUSTOM_TYPE) {
    return { type: "gif", url: text };
  }

  if (message instanceof FileMessage) {
    const media = toMedia(message, ctx);
    if (media) {
      return { type: "media", media, caption: toMediaCaption(text, message.name) };
    }
    return {
      type: "unsupported",
      text: message.name || text,
      customType: customType || null,
      rawData: data ?? null,
    };
  }

  if (customType === ACTIVITY_CUSTOM_TYPE) {
    return {
      type: "activity",
      text,
      customType,
      rawData: data ?? null,
      activity: parseAttentionActivityPayload(data),
    };
  }

  if (customType === ENTITY_SHARED_CUSTOM_TYPE) {
    return {
      type: "entityShared",
      text,
      customType,
      rawData: data ?? null,
      entity: parseAttentionEntityPayload(data),
    };
  }

  if (
    customType === ENTITY_INTERACTION_CUSTOM_TYPE ||
    customType === ACTIVITY_INTERACTION_CUSTOM_TYPE
  ) {
    return {
      type: "entityInteraction",
      text,
      customType,
      rawData: data ?? null,
      entity: parseAttentionEntityPayload(data),
    };
  }

  if (
    customType === GAME_START_CUSTOM_TYPE ||
    customType === GAME_END_CUSTOM_TYPE
  ) {
    return {
      type: "game",
      customType,
      phase: customType === GAME_START_CUSTOM_TYPE ? "start" : "end",
      rawData: data ?? null,
    };
  }

  if (customType === SUPERVISION_CUSTOM_TYPE) {
    return { type: "supervision", text, translations: getTranslations(message) };
  }

  if (message instanceof AdminMessage) {
    return { type: "system", text };
  }

  return {
    type: "text",
    text,
    translations: getTranslations(message),
    rawData: data ?? null,
  };
}

function toQuote(
  parent: BaseMessage,
  ctx: AttentionAdapterContext,
): AttentionQuote {
  return deriveAttentionQuote(toAttentionMessage(parent, ctx));
}

function defaultReadState(direction: string): AttentionReadState {
  return direction === "outgoing" ? "sent" : "unknown";
}

// Only optimistic locals carry "pending"/"failed"; every other status is "sent".
export function resolveSendState(message: BaseMessage): AttentionSendState {
  const status = (message as { sendingStatus?: string }).sendingStatus;
  switch (status) {
    case "pending":
      return "pending";
    case "failed":
      return "failed";
    default:
      return "sent";
  }
}

export function toAttentionMessage(
  message: BaseMessage,
  ctx: AttentionAdapterContext,
): AttentionMessage {
  const sender = getSender(message);
  const direction = resolveAttentionDirection(
    sender?.userId ?? null,
    ctx.currentUserId,
  );
  const parent = (message as { parentMessage?: BaseMessage | null })
    .parentMessage;

  return {
    id: getStableMessageId(message),
    direction,
    isSelf: direction === "outgoing",
    sender: toAttentionSender(sender, ctx),
    createdAt: DateTime.fromMillis(message.createdAt).toISO() ?? "",
    readState: ctx.resolveReadState?.(message) ?? defaultReadState(direction),
    sendState: resolveSendState(message),
    content: toContent(message, ctx),
    quote: parent ? toQuote(parent, ctx) : null,
    rawCustomType: message.customType || null,
  };
}

export function toAttentionMessages(
  messages: BaseMessage[],
  ctx: AttentionAdapterContext,
): AttentionMessage[] {
  return messages.map((message) => toAttentionMessage(message, ctx));
}